import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

import PresentationEditorController from "../PresentationEditorController";

const themes = {
    light: {
        background: "#E8E8E8",
        toolbar: "#FFFFFF",
        leftPanel: "#F5F5F5",
        rightSidebar: "#FFFFFF",
        shimmerFrom: "#D0D0D0",
        shimmerTo: "#E5E5E5",
        slideNumber: "#666",
        scrollbar: "#D8D8D8",
    },
    dark: {
        background: "#3A3A3A",
        toolbar: "#2D2D2D",
        leftPanel: "#2D2D2D",
        rightSidebar: "#2D2D2D",
        shimmerFrom: "#4A4A4A",
        shimmerTo: "#5A5A5A",
        slideNumber: "#888",
        scrollbar: "#555",
    },
};

const shimmer = keyframes`
    0% {
        background-position: -1000px 0;
    }
    100% {
        background-position: 1000px 0;
    }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

const SkeletonContainer = styled.div<{ isFadingOut: boolean; themeColors: typeof themes.dark }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.themeColors.background};
    z-index: 10;
    animation: ${props => props.isFadingOut ? fadeOut : "none"} 0.5s ease-out forwards;
    display: flex;
    flex-direction: column;
`;

const SkeletonBox = styled.div<{ themeColors: typeof themes.dark }>`
    background: linear-gradient(
        90deg,
        ${props => props.themeColors.shimmerFrom} 0%,
        ${props => props.themeColors.shimmerTo} 20%,
        ${props => props.themeColors.shimmerFrom} 40%,
        ${props => props.themeColors.shimmerFrom} 100%
    );
    background-size: 1000px 100%;
    animation: ${shimmer} 2s infinite linear;
    border-radius: 4px;
`;

const TopToolbar = styled.div<{ themeColors: typeof themes.dark }>`
    height: 55px;
    width: 100%;
    background-color: ${props => props.themeColors.toolbar};
    display: flex;
    align-items: center;
    padding: 0 16px;
    gap: 16px;
    flex-shrink: 0;
`;

const ToolbarButton = styled(SkeletonBox)`
    height: 24px;
    width: 24px;
    border-radius: 3px;
`;

const ToolbarDropdown = styled(SkeletonBox)`
    height: 28px;
    width: 120px;
`;

const ToolbarTitle = styled(SkeletonBox)`
    height: 20px;
    width: 300px;
`;

const MainContent = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
`;

const LeftSlidebar = styled.div<{ themeColors: typeof themes.dark }>`
    width: 280px;
    background-color: ${props => props.themeColors.leftPanel};
    display: flex;
    flex-direction: column;
    padding: 16px 12px;
    gap: 8px;
    flex-shrink: 0;
    overflow-y: auto;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 12px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        margin-top: 16px;
        margin-right: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${props => props.themeColors.scrollbar};
        border-radius: 4px;
        border: 3px solid transparent;
        background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: ${props => props.themeColors.scrollbar}cc;
        background-clip: padding-box;
    }

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${props => props.themeColors.scrollbar} transparent;
`;

const SlideItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
`;

const SlideNumber = styled.div<{ themeColors: typeof themes.dark }>`
    color: ${props => props.themeColors.slideNumber};
    font-size: 14px;
    width: 20px;
    text-align: right;
    flex-shrink: 0;
    padding-top: 2px;
`;

const SlideThumb = styled(SkeletonBox)`
    flex: 1;
    aspect-ratio: 16 / 9;
    border-radius: 4px;
`;

const AddSlideButton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    flex-shrink: 0;
`;

const AddSlideIcon = styled(SkeletonBox)`
    height: 32px;
    width: 32px;
    border-radius: 50%;
`;

const AddSlideText = styled(SkeletonBox)`
    height: 20px;
    width: 100px;
`;

const CenterCanvas = styled.div<{ themeColors: typeof themes.dark }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 40px;
    background-color: ${props => props.themeColors.background};
    position: relative;
    max-width: 1427px;
    margin: 0 auto;
`;

const SlidePreview = styled(SkeletonBox)`
    width: 100%;
    max-width: 1200px;
    aspect-ratio: 16 / 9;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const SlideLabel = styled(SkeletonBox)`
    width: 120px;
    height: 32px;
    margin-top: 20px;
`;

const RightSidebar = styled.div<{ themeColors: typeof themes.dark }>`
    width: 70px;
    background-color: ${props => props.themeColors.rightSidebar};
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 0;
    gap: 20px;
    flex-shrink: 0;
`;

const SidebarButton = styled(SkeletonBox)`
    width: 32px;
    height: 32px;
    border-radius: 4px;
`;

const GridViewButton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 0;
    border-top: 1px solid #2A2A2A;
    flex-shrink: 0;
`;

const GridViewIcon = styled(SkeletonBox)`
    height: 20px;
    width: 20px;
`;

const GridViewText = styled(SkeletonBox)`
    height: 16px;
    width: 80px;
`;

function EditorSkeleton({ isInitialized, theme = "dark" }: { isInitialized: boolean, theme?: "light" | "dark" }) {
    const [isVisible, setIsVisible] = useState(true);
    const themeColors = themes[theme];

    useEffect(() => {
        if (isInitialized) {
            const timer = setTimeout(() => setIsVisible(false), 500);
            return () => clearTimeout(timer);
        }
    }, [isInitialized]);

    if (!isVisible) {
        return null;
    }

    return (
        <SkeletonContainer isFadingOut={isInitialized} themeColors={themeColors}>
            <TopToolbar themeColors={themeColors}>
                <ToolbarButton themeColors={themeColors} />
                <ToolbarDropdown themeColors={themeColors} />
                <ToolbarDropdown themeColors={themeColors} />
                <ToolbarButton themeColors={themeColors} />
                <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                    <ToolbarTitle themeColors={themeColors} />
                </div>
                <ToolbarButton themeColors={themeColors} />
                <ToolbarButton themeColors={themeColors} />
                <ToolbarButton themeColors={themeColors} />
                <ToolbarButton themeColors={themeColors} />
            </TopToolbar>
            <MainContent>
                <LeftSlidebar themeColors={themeColors}>
                    <SlideItem>
                        <SlideNumber themeColors={themeColors}>1</SlideNumber>
                        <SlideThumb themeColors={themeColors} />
                    </SlideItem>
                    <SlideItem>
                        <SlideNumber themeColors={themeColors}>2</SlideNumber>
                        <SlideThumb themeColors={themeColors} />
                    </SlideItem>
                    <SlideItem>
                        <SlideNumber themeColors={themeColors}>3</SlideNumber>
                        <SlideThumb themeColors={themeColors} />
                    </SlideItem>
                    <SlideItem>
                        <SlideNumber themeColors={themeColors}>4</SlideNumber>
                        <SlideThumb themeColors={themeColors} />
                    </SlideItem>
                    <SlideItem>
                        <SlideNumber themeColors={themeColors}>5</SlideNumber>
                        <SlideThumb themeColors={themeColors} />
                    </SlideItem>
                    <SlideItem>
                        <SlideNumber themeColors={themeColors}>6</SlideNumber>
                        <SlideThumb themeColors={themeColors} />
                    </SlideItem>
                    <SlideItem>
                        <SlideNumber themeColors={themeColors}>7</SlideNumber>
                        <SlideThumb themeColors={themeColors} />
                    </SlideItem>
                    <AddSlideButton>
                        <div style={{ width: "20px" }} />
                        <AddSlideIcon themeColors={themeColors} />
                        <AddSlideText themeColors={themeColors} />
                    </AddSlideButton>
                    <GridViewButton>
                        <div style={{ width: "20px" }} />
                        <GridViewIcon themeColors={themeColors} />
                        <GridViewText themeColors={themeColors} />
                    </GridViewButton>
                </LeftSlidebar>
                <CenterCanvas themeColors={themeColors}>
                    <SlidePreview themeColors={themeColors} />
                    <SlideLabel themeColors={themeColors} />
                </CenterCanvas>
                <RightSidebar themeColors={themeColors}>
                    <SidebarButton themeColors={themeColors} />
                    <SidebarButton themeColors={themeColors} />
                    <SidebarButton themeColors={themeColors} />
                    <SidebarButton themeColors={themeColors} />
                    <SidebarButton themeColors={themeColors} />
                    <SidebarButton themeColors={themeColors} />
                    <SidebarButton themeColors={themeColors} />
                    <SidebarButton themeColors={themeColors} />
                </RightSidebar>
            </MainContent>
        </SkeletonContainer>
    );
}

export default EditorSkeleton;
